/**
 * パスワードの欄 + 表示の切り替え（目のアイコン）（2026-09-28 creator/login-redesign・指示書 4）。/login・/signup で使う。
 *
 * 約束:
 *   - 属性（id・name・autocomplete・passwordrules）は呼ぶ側が authForms.ts の passwordFieldProps で作って渡す。
 *     ここは type だけを変える（authForms.ts の withPasswordVisibility）= パスワード管理の保存・生成・自動入力を壊さない
 *   - 切り替えのボタンは type="button"（form の中にあるので、既定の submit だと押しただけでログインが送られる）
 *   - aria-label（見せる / 隠す）+ aria-pressed + aria-controls。アイコンだけのボタンなので名前を必ず持たせる
 *   - 送信の直前に隠す（hideSignal が変わったら隠す）。WHY: 表示中の type=text のまま送ると、「パスワードの送信」と見ない
 *     パスワード管理がある。画面共有中に見えたままにもしない
 *   - 見出しの行の右に、欄に関係する導線（「パスワードをお忘れですか？」）を置ける（aside）。
 *     WHY <label> で欄を包まないか: 包むと、見出しの行のリンクや目のボタンを押したときにラベルの「欄にフォーカス」と混ざる。
 *     htmlFor で結ぶ（<label for> はパスワード管理も読む）
 * アイコンはインラインの SVG（CSP で外から画像を読まない = src/csp.ts の img-src を増やさない）。
 */
import { useEffect, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { withPasswordVisibility } from '../authForms';
import { useT } from '../i18n';

type FieldProps = InputHTMLAttributes<HTMLInputElement> & { id: string; type: string; passwordrules?: string };

export function PasswordField({
  label,
  aside,
  hint,
  inputProps,
  hideSignal
}: {
  label: ReactNode;
  /** 見出しの行の右（「パスワードをお忘れですか？」のリンク等） */
  aside?: ReactNode;
  /** 欄の下の注記（aria-describedby は inputProps 側で指す） */
  hint?: ReactNode;
  inputProps: FieldProps;
  /** 変わったら隠す（送信の回数など） */
  hideSignal?: unknown;
}) {
  const { t } = useT();
  const [visible, setVisible] = useState(false);
  useEffect(() => setVisible(false), [hideSignal]);
  const id = inputProps.id;
  return (
    <div className="field">
      <div className="field-head">
        <label htmlFor={id}>{label}</label>
        {aside}
      </div>
      <div className="pw-wrap">
        <input {...withPasswordVisibility(inputProps, visible)} />
        <button
          type="button"
          className="pw-toggle"
          aria-label={visible ? t('auth.hidePassword') : t('auth.showPassword')}
          aria-pressed={visible}
          aria-controls={id}
          // マウスで押してもフォーカスを欄から奪わない（入力の途中で確かめて、そのまま打ち続けられる）
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setVisible((v) => !v)}
        >
          {visible ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
      {hint}
    </div>
  );
}

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.6 5.1A10.4 10.4 0 0 1 12 5c6.4 0 10 7 10 7a17.6 17.6 0 0 1-2.9 3.9" />
      <path d="M6.6 6.6C3.8 8.4 2 12 2 12s3.6 7 10 7a9.7 9.7 0 0 0 5.4-1.6" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
      <path d="M2 2l20 20" />
    </svg>
  );
}
